import type { ConversationPlacement } from '@crowdex/agent-contracts/api';
import { Button } from '@crowdex/ui/components/button';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuTrigger,
} from '@crowdex/ui/components/context-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@crowdex/ui/components/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '@crowdex/ui/components/dropdown-menu';
import { showToast } from '@crowdex/ui/components/toaster';
import { cn } from '@crowdex/ui/lib/utils';
import {
  Archive,
  Check,
  CircleStop,
  Columns2,
  FolderMinus,
  FolderOutput,
  Laptop,
  Link,
  type LucideIcon,
  MessageCircle,
  MoreHorizontal,
  Pencil,
  Pin,
  PinOff,
  Search,
  Share,
  SlidersHorizontal,
  Trash2,
} from 'lucide-react';
import { Fragment, type ReactNode, useEffect, useState } from 'react';
import { SHEET_ON_PHONE } from '@/components/desktop-shell/dialog-sheet';
import { useNotifications } from '@/features/notifications/notifications';
import { useWorkspaceDirectory, workspaceDirectoryStore } from '@/lib/assistants/directory';
import { loadCloudLink, onCloudLinkChanged } from '@/lib/cloud-link';
import { togglePinnedConversation, usePinnedConversations } from '@/lib/conversation-pins';
import { useOptionalDesktopShellStoreApi } from '@/lib/desktop-shell/store-context';
import { isLocalMachine } from '@/lib/local-runtime';
import { type KeyboardShortcut, shortcutLabel } from '@/lib/platform';
import {
  ARCHIVE_SHORTCUT,
  currentShortcut,
  FIND_SHORTCUT,
  RENAME_SHORTCUT,
  SIDE_CHAT_SHORTCUT,
  useShortcutOverrides,
} from '@/lib/settings-page/keyboard-shortcuts';
import { showPanelView } from '@/lib/side-panel/panel-tabs';
import type { SidebarThread } from '@/lib/sidebar-threads';
import { type ShellNavigation, useShellNavigation } from './shell-navigation';

/** Opens the find bar of the conversation shown (its pane listens). */
export const FIND_IN_CONVERSATION_EVENT = 'crowdex:find-in-conversation';

/** The conversation actions, by id: one name each, on every surface. */
export type ConversationActionId =
  | 'pin'
  | 'rename'
  | 'remove-from-assistant'
  | 'detach'
  | 'read'
  | 'split-view'
  | 'side-chat'
  | 'continue-locally'
  | 'find'
  | 'share'
  | 'repository-setup'
  | 'stop'
  | 'archive'
  | 'delete';

/**
 * One action of a conversation. The desktop's ⋮ (D6), a row's right click (D22), the phone's ⋮
 * (4) and its long press (19) all list these, with the same names and in the same order.
 */
export type ConversationAction = {
  id: ConversationActionId;
  label: string;
  icon: LucideIcon;
  /** A second line: the project it is in. */
  detail?: string | undefined;
  /** The desktop's keys (Settings › Keyboard shortcuts may change them). */
  shortcut?: KeyboardShortcut | undefined;
  destructive?: boolean;
  /** A separator goes before it. */
  separated?: boolean;
  /** Runs it; a submenu's entries instead (Open in split view, from the conversation shown). */
  run?: (() => void) | undefined;
  submenu?: readonly { id: string; label: string; run: () => void }[];
};

/** "Open in split view" offers the most recent conversations, other than the one shown. */
const SPLIT_VIEW_LIMIT = 6;

/** ⌥↵: a conversation of the list opens in split view beside the one shown. */
const SPLIT_VIEW_SHORTCUT: KeyboardShortcut = { key: 'Enter', alt: true };

/** A toast: the conversation was not archived, renamed… (with why). */
export function failed(what: string) {
  return (reason: unknown) =>
    showToast({
      title: `The conversation was not ${what}.`,
      ...(reason instanceof Error ? { description: reason.message } : {}),
      tone: 'error',
    });
}

/** What the surface asking does for the conversation, beyond what the shell does. */
export type ConversationActionHandlers = {
  rename: () => void;
  delete: () => void;
  markRead: () => void;
  /** The conversation shown: its side chat (a tab of its panel, D52). */
  sideChat?: (() => void) | undefined;
  /** The conversation shown: its find bar. */
  find?: (() => void) | undefined;
  /** The conversation shown, in the cloud: bring it to a computer (the handoff, D50). */
  continueLocally?: (() => void) | undefined;
  /** A link to it: shared with the system's sheet, else copied. */
  share?: { kind: 'share' | 'copy'; run: () => void } | undefined;
  /** Its repository's setup script and variables (D47). */
  repositorySetup?: (() => void) | undefined;
};

/** The keys of an action: the user's (Settings › Keyboard shortcuts), else its own. */
export type ShortcutKeys = (id: string, fallback: KeyboardShortcut) => KeyboardShortcut;

/**
 * The actions of a conversation, in one order everywhere. An action that cannot act here (stop
 * a turn the shell does not run, find in a conversation that is not shown) is hidden, not
 * disabled.
 */
export function conversationActions(input: {
  thread: SidebarThread;
  navigation: ShellNavigation;
  surface: 'desktop' | 'phone';
  pinned: boolean;
  unread: boolean;
  running: boolean;
  /** The conversation the window shows: side chat, find, stop and the handoff act on it. */
  shown: boolean;
  /** Where it sits: a child conversation can be detached from its parent (D49). */
  placement?: ConversationPlacement | null;
  /**
   * The assistant it is filed in, under "Remove from assistant" (never set for an assistant's
   * own channel). Nothing files a conversation into an assistant by hand: an assistant is its
   * conversation, and its work is what it delegates (docs/adr-assistants.md).
   */
  projectName?: string | null;
  handlers: ConversationActionHandlers;
  keys?: ShortcutKeys;
  /**
   * The conversation shown offers the others to open beside it (its ⋮); a row of it has no
   * split to open (it is in the window already).
   */
  splitWithOthers?: boolean;
}): ConversationAction[] {
  const { thread, navigation, pinned, unread, running, shown, handlers } = input;
  const desktop = input.surface === 'desktop';
  // A phone has no keys to show.
  const keys = (id: string, fallback: KeyboardShortcut) =>
    desktop ? (input.keys?.(id, fallback) ?? fallback) : undefined;
  const actions: (ConversationAction | false)[] = [
    {
      id: 'pin',
      label: pinned ? 'Unpin' : 'Pin',
      icon: pinned ? PinOff : Pin,
      run: () => togglePinnedConversation(thread.id),
    },
    navigation.renameConversation !== undefined && {
      id: 'rename',
      label: 'Rename',
      icon: Pencil,
      shortcut: keys('rename', RENAME_SHORTCUT),
      run: handlers.rename,
    },
  ];
  const placement = input.placement ?? null;
  const projectName = input.projectName ?? null;
  if (placement?.projectId != null && projectName !== null)
    actions.push({
      id: 'remove-from-assistant',
      label: 'Remove from assistant',
      detail: projectName,
      icon: FolderMinus,
      run: () => void removeFromAssistant(thread),
    });
  if (placement?.parentConversationId != null)
    actions.push({
      id: 'detach',
      label: 'Detach',
      icon: FolderOutput,
      run: () => void detachConversation(thread, placement),
    });
  if (unread)
    actions.push({ id: 'read', label: 'Mark as read', icon: Check, run: handlers.markRead });
  const openSideBySide = navigation.openSideBySide;
  if (desktop && openSideBySide !== undefined) {
    if (!shown)
      actions.push({
        id: 'split-view',
        label: 'Open in split view',
        icon: Columns2,
        shortcut: SPLIT_VIEW_SHORTCUT,
        run: () => openSideBySide(thread),
      });
    else if (input.splitWithOthers === true) {
      // The ⋮ of the conversation shown opens another one beside it.
      const others = navigation.threads
        .filter((candidate) => candidate.id !== thread.id)
        .slice(0, SPLIT_VIEW_LIMIT);
      if (others.length > 0)
        actions.push({
          id: 'split-view',
          label: 'Open in split view',
          icon: Columns2,
          submenu: others.map((other) => ({
            id: other.id,
            label: other.title,
            run: () => openSideBySide(other),
          })),
        });
    }
  }
  actions.push(
    shown &&
      handlers.sideChat !== undefined && {
        id: 'side-chat',
        label: 'Side chat',
        icon: MessageCircle,
        shortcut: keys('side-chat', SIDE_CHAT_SHORTCUT),
        run: handlers.sideChat,
      },
    shown &&
      handlers.continueLocally !== undefined && {
        id: 'continue-locally',
        // A phone hands it to one of the user's computers (45); the desktop is the computer.
        label: desktop ? 'Continue on this computer' : 'Continue on a computer',
        icon: Laptop,
        run: handlers.continueLocally,
      },
    shown &&
      handlers.find !== undefined && {
        id: 'find',
        label: 'Find in conversation',
        icon: Search,
        shortcut: keys('find', FIND_SHORTCUT),
        run: handlers.find,
      },
    handlers.share !== undefined && {
      id: 'share',
      label: handlers.share.kind === 'copy' ? 'Copy link' : 'Share',
      icon: handlers.share.kind === 'copy' ? Link : Share,
      run: handlers.share.run,
    },
    handlers.repositorySetup !== undefined && {
      id: 'repository-setup',
      label: 'Repository setup',
      icon: SlidersHorizontal,
      run: handlers.repositorySetup,
    },
    running &&
      shown &&
      navigation.stopAgent !== undefined && {
        id: 'stop',
        label: 'Stop the agent',
        icon: CircleStop,
        run: () => navigation.stopAgent?.(thread),
      },
    navigation.archiveConversation !== undefined && {
      id: 'archive',
      label: 'Archive',
      icon: Archive,
      shortcut: keys('archive', ARCHIVE_SHORTCUT),
      separated: true,
      run: () => void navigation.archiveConversation?.(thread).catch(failed('archived')),
    },
    navigation.deleteConversation !== undefined && {
      id: 'delete',
      label: 'Delete',
      icon: Trash2,
      destructive: true,
      separated: navigation.archiveConversation === undefined,
      run: handlers.delete,
    },
  );
  return actions.filter((action): action is ConversationAction => action !== false);
}

/**
 * "Remove from assistant": the conversation (and its children) leaves its assistant, a plain
 * conversation again.
 */
async function removeFromAssistant(thread: Pick<SidebarThread, 'id'>): Promise<void> {
  await workspaceDirectoryStore()
    .getState()
    .moveConversation(thread.id, { projectId: null })
    .catch(failed('removed from its assistant'));
}

/**
 * "Detach" (rare): the child conversation leaves its parent and stays in its project, at the
 * first level of the list.
 */
async function detachConversation(
  thread: Pick<SidebarThread, 'id' | 'title'>,
  placement: ConversationPlacement,
): Promise<void> {
  await workspaceDirectoryStore()
    .getState()
    .moveConversation(thread.id, {
      projectId: placement.projectId,
      parentConversationId: null,
    })
    .catch(failed('detached'));
}

/**
 * Where a conversation's link points. On a server of the web (Crowdex Cloud) the page's own
 * address is one others can open: it is shared. On this computer (local, the Desktop app) it is
 * not: a conversation living on the linked Crowdex Cloud gets the cloud's public address, any
 * other only a link to copy (it opens here alone).
 */
export type ShareLink = { kind: 'share'; origin: string } | { kind: 'copy'; origin: string };

/** The link of a conversation; `enabled` false reads nothing (a row not shown). */
export function useShareLink(onCloud: boolean, enabled = true): ShareLink {
  const local = isLocalMachine();
  const [cloudOrigin, setCloudOrigin] = useState<string | null>(null);
  useEffect(() => {
    if (!local || !enabled) return;
    let current = true;
    const read = () =>
      void loadCloudLink().then(
        (link) => {
          if (current) setCloudOrigin(link.account === null ? null : link.url);
        },
        () => {
          if (current) setCloudOrigin(null);
        },
      );
    read();
    const stop = onCloudLinkChanged(read);
    return () => {
      current = false;
      stop();
    };
  }, [local, enabled]);
  const origin = typeof window === 'undefined' ? '' : window.location.origin;
  if (!local) return { kind: 'share', origin };
  if (onCloud && cloudOrigin !== null) return { kind: 'share', origin: cloudOrigin };
  return { kind: 'copy', origin };
}

/** A link to the conversation: shared with the system's sheet where there is one, else copied. */
async function shareConversation(
  conversationId: string,
  title: string,
  link: ShareLink,
): Promise<void> {
  const url = new URL('/', link.origin);
  url.searchParams.set('threadId', conversationId);
  try {
    if (link.kind === 'share' && typeof navigator.share === 'function') {
      await navigator.share({ title, url: url.toString() });
      return;
    }
    await navigator.clipboard.writeText(url.toString());
    showToast({ title: 'Link copied', description: title, tone: 'success' });
  } catch (reason) {
    // The user closing the share sheet is no failure.
    if (reason instanceof DOMException && reason.name === 'AbortError') return;
    failed('shared')(reason);
  }
}

/**
 * The name of the assistant a conversation is filed in, if it is in one; none for an
 * assistant's own channel (it is the assistant).
 */
export function useConversationProjectName(conversationId: string): string | null {
  return useWorkspaceDirectory((state) => {
    const projectId = state.placements[conversationId]?.projectId;
    if (projectId == null) return null;
    if (state.projects.some((project) => project.conversationId === conversationId)) return null;
    return state.projects.find((project) => project.id === projectId)?.name ?? null;
  });
}

/**
 * The actions of one conversation, with the state they need (pinned, unread, the delete
 * dialog): what a row's right click, the phone's long press and both ⋮ menus list. The
 * conversation shown gets its side chat, find, share and handoff too; `handlers` adds or
 * replaces what the surface does itself.
 */
export function useConversationActions(
  thread: SidebarThread,
  options: {
    running: boolean;
    surface: 'desktop' | 'phone';
    onRename: () => void;
    /** It is the conversation the window shows (by default, the shell's active one). */
    shown?: boolean;
    handlers?: Partial<ConversationActionHandlers>;
    /** The ⋮ of the conversation shown: "Open in split view" lists the others. */
    splitWithOthers?: boolean;
  },
) {
  const navigation = useShellNavigation();
  const notifications = useNotifications();
  const store = useOptionalDesktopShellStoreApi();
  const overrides = useShortcutOverrides();
  const pinned = usePinnedConversations().has(thread.id);
  const projectName = useConversationProjectName(thread.id);
  const placement =
    useWorkspaceDirectory((state) => state.placements[thread.id]) ?? thread.placement ?? null;
  const shown = options.shown ?? thread.id === navigation.activeThreadId;
  const share = useShareLink(thread.runtime === 'cloud', shown);
  const [deleting, setDeleting] = useState(false);
  const unread =
    thread.unread === true ||
    (notifications?.notifications.some(
      (entry) => entry.threadId === thread.id && entry.readAt === null,
    ) ??
      false);
  const continueLocally = navigation.continueLocally;
  const actions = conversationActions({
    thread,
    navigation,
    pinned,
    unread,
    running: options.running,
    surface: options.surface,
    shown,
    placement,
    projectName,
    keys: (id, fallback) => currentShortcut(id, overrides) ?? fallback,
    splitWithOthers: options.splitWithOthers,
    handlers: {
      rename: options.onRename,
      delete: () => setDeleting(true),
      markRead: () => notifications?.markRead({ threadId: thread.id }),
      sideChat:
        store === null
          ? undefined
          : () => {
              store.getState().setPanelTabs((tabs) => showPanelView(tabs, 'chat'));
              store.getState().setSidePanelOpen(true);
            },
      find: () => window.dispatchEvent(new Event(FIND_IN_CONVERSATION_EVENT)),
      continueLocally:
        thread.runtime === 'cloud' && continueLocally !== undefined
          ? () => continueLocally(thread)
          : undefined,
      // Its link is read for the conversation shown only (a row would ask the cloud link each).
      share: shown
        ? { kind: share.kind, run: () => void shareConversation(thread.id, thread.title, share) }
        : undefined,
      ...options.handlers,
    },
  });
  const confirmDelete = (
    <Dialog onOpenChange={setDeleting} open={deleting}>
      <DialogContent className={cn('sm:max-w-sm', SHEET_ON_PHONE)}>
        <DialogTitle>Delete “{thread.title}”?</DialogTitle>
        <DialogDescription>
          Its messages are deleted for good. Archive it instead to keep it findable{' '}
          {options.surface === 'phone' ? 'in search' : 'with ⌘K'}.
        </DialogDescription>
        <div className="mt-2 flex justify-end gap-2 max-md:[&>button]:h-12 max-md:[&>button]:flex-1">
          <Button onClick={() => setDeleting(false)} variant="outline">
            Cancel
          </Button>
          <Button
            onClick={() => {
              setDeleting(false);
              void navigation.deleteConversation?.(thread).catch(failed('deleted'));
            }}
            variant="destructive"
          >
            Delete
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
  return { actions, confirmDelete, navigation };
}

/** An entry of a row's menu: a conversation's action, or a project's (same shape). */
export type RowMenuAction = Omit<ConversationAction, 'id'> & { id: string };

/** A row's right-click menu (D22), titled with its conversation or project. */
export function ConversationContextMenu({
  title,
  actions,
  children,
}: {
  title: string;
  actions: readonly RowMenuAction[];
  children: ReactNode;
}) {
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent aria-label={`Actions for ${title}`} className="w-64">
        <ContextMenuLabel>{title}</ContextMenuLabel>
        {actions.map((action) => (
          <ConversationContextMenuItem action={action} key={action.id} />
        ))}
      </ContextMenuContent>
    </ContextMenu>
  );
}

function ConversationContextMenuItem({ action }: { action: RowMenuAction }) {
  const Icon = action.icon;
  return (
    <>
      {action.separated && <ContextMenuSeparator />}
      {action.run !== undefined && (
        <ContextMenuItem
          className="min-h-9 gap-2.5"
          onSelect={action.run}
          variant={action.destructive ? 'destructive' : 'default'}
        >
          <Icon />
          {action.label}
          {action.shortcut !== undefined && (
            // Full muted ink: the dimmer default does not reach 4.5:1 on the menu.
            <ContextMenuShortcut className="text-muted-foreground">
              {shortcutLabel(action.shortcut)}
            </ContextMenuShortcut>
          )}
        </ContextMenuItem>
      )}
    </>
  );
}

/**
 * The "⋯" of a row, on hover or focus: the same actions as its context menu, for a pointer
 * that does not right-click and for the keyboard.
 */
export function ConversationActionsButton({
  title,
  actions,
  className,
}: {
  title: string;
  actions: readonly RowMenuAction[];
  className?: string;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label={`More actions for ${title}`}
          className={className}
          title="More actions"
          type="button"
        >
          <MoreHorizontal className="size-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>{title}</DropdownMenuLabel>
        {actions.map((action) => {
          if (action.run === undefined) return null;
          const Icon = action.icon;
          return (
            <Fragment key={action.id}>
              {action.separated && <DropdownMenuSeparator />}
              <DropdownMenuItem
                className="min-h-9 gap-2.5"
                onSelect={action.run}
                variant={action.destructive ? 'destructive' : 'default'}
              >
                <Icon />
                {action.label}
                {action.shortcut !== undefined && (
                  <DropdownMenuShortcut className="text-muted-foreground">
                    {shortcutLabel(action.shortcut)}
                  </DropdownMenuShortcut>
                )}
              </DropdownMenuItem>
            </Fragment>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
